import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { getDashboardColors } from "../../utils/dashboardColors";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const cssVar = (name) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim();

const CourseSubjectAnalysisCard = ({ subjects = [] }) => {
  const normalizedSubjects = useMemo(() => {
    if (Array.isArray(subjects)) return subjects;
    if (subjects && typeof subjects === "object")
      return Object.values(subjects);
    return [];
  }, [subjects]);

  const labels = useMemo(
    () => normalizedSubjects.map((s) => s.subject_name || s.name || "Unknown"),
    [normalizedSubjects],
  );

  const values = useMemo(
    () =>
      normalizedSubjects.map((s) => s.percentage || s.total_percentage || 0),
    [normalizedSubjects],
  );

  const overallPercentage = useMemo(() => {
    if (!values.length) return 0;
    const sum = values.reduce((a, b) => a + b, 0);
    return Math.round(sum / values.length);
  }, [values]);

  const barColor = getDashboardColors().accentColor || "#FCA43B";

  const chartData = useMemo(
    () => ({
      labels: labels.length ? labels : ["No Data"],
      datasets: [
        {
          label: "Completion %",
          data: values.length ? values : [0],
          backgroundColor: (context) => {
            const chart = context.chart;
            const { ctx, chartArea } = chart;
            if (!chartArea) return barColor;
            const gradient = ctx.createLinearGradient(
              0,
              chartArea.top,
              0,
              chartArea.bottom,
            );
            gradient.addColorStop(0, barColor);
            gradient.addColorStop(1, "rgba(252, 164, 59, 0.4)");
            return gradient;
          },
          hoverBackgroundColor: barColor,
          borderRadius: 8,
          barThickness: 32,
          hoverBorderWidth: 2,
          hoverBorderColor: "#fff",
        },
      ],
    }),
    [labels, values, barColor],
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 2000,
        easing: "easeOutQuart",
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "rgba(33, 43, 54, 0.9)",
          padding: 12,
          bodyFont: { size: 13, weight: "600" },
          displayColors: false,
          callbacks: {
            title: (ctx) => {
              const index = ctx?.[0]?.dataIndex ?? 0;
              return labels[index] || "";
            },
            label: (ctx) => `${ctx.parsed.y}% Complete`,
          },
        },
      },
      scales: {
        y: {
          min: 0,
          max: 100,
          grid: { color: "rgba(145, 158, 171, 0.1)", drawBorder: false },
          ticks: { stepSize: 20, color: "#919EAB", font: { size: 10 } },
        },
        x: {
          grid: { display: false },
          ticks: {
            color: "#919EAB",
            font: { size: 10 },
            callback: (value, index) => {
              const label = labels[index] || "";
              if (!label) return "";
              const maxLen = 10;
              return label.length > maxLen
                ? `${label.slice(0, maxLen)}...`
                : label;
            },
            maxRotation: 0,
            autoSkip: false,
          },
        },
      },
    }),
    [labels],
  );

  return (
    <div className="card-styled-wrapper course-subject-card">
      <div className="course-subject-card__content">
        <p className="course-subject-title-main">Course Completion</p>
        <div className="course-subject-header">
          <p className="course-subject-title">Overall Progress</p>

          <div className="course-subject-header-progress">
            <div className="course-subject-progress-track">
              <div
                className="course-subject-progress-fill"
                style={{ width: `${overallPercentage}%` }}
              />
            </div>
            <span className="course-subject-title-value">
              {overallPercentage}%
            </span>
          </div>
        </div>

        <div style={{ display: "flex", height: 300, width: "100%" }}>
          <div
            style={{
              width: 50,
              flexShrink: 0,
              position: "relative",
              zIndex: 1,
              backgroundColor: "#fff",
            }}
          >
            <Bar
              data={{
                labels: labels,
                datasets: [{ data: [], label: "" }],
              }}
              options={{
                ...chartOptions,
                plugins: {
                  legend: { display: false },
                  tooltip: { enabled: false },
                },
                scales: {
                  ...chartOptions.scales,
                  x: {
                    ...chartOptions.scales.x,
                    ticks: { color: "transparent" },
                    grid: { display: false, drawBorder: false },
                  },
                  y: {
                    ...chartOptions.scales.y,
                    afterFit: (axis) => {
                      axis.width = 50;
                    },
                  },
                },
                layout: { padding: 0 },
                maintainAspectRatio: false,
              }}
            />
          </div>

          <div
            className="course-subject-chart-scroll-container custom-scrollbar"
            style={{
              overflowX: "auto",
              flexGrow: 1,
              width: "calc(100% - 50px)",
            }}
          >
            <div
              className="course-subject-chart"
              style={{
                width: `${Math.max(labels.length * 60, 500)}px`,
              }}
            >
              <Bar
                data={chartData}
                options={{
                  ...chartOptions,
                  scales: {
                    ...chartOptions.scales,
                    y: {
                      ...chartOptions.scales.y,
                      grid: {
                        color: "rgba(145, 158, 171, 0.1)",
                        drawBorder: false,
                        drawTicks: false,
                      },
                      ticks: { display: false },
                      border: { display: false },
                    },
                  },
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseSubjectAnalysisCard;
